<template>
  <div class="generated-quiz-questions">
    <section
      class="flex flex-col md:flex-row justify-center md:justify-between items-center gap-3 bg-blue-100 font-bold p-4 rounded-md shadow-md mb-4"
    >
      <p>
        {{
          $t("labels.generic", {
            pt: "Questões Geradas",
            en: "Generated Questions",
          })
        }}
      </p>
      <p>
        {{ generatedQuestions.length }}
        {{ $t("labels.generic", { pt: "Questões", en: "Questions" }) }}
      </p>
    </section>

    <div class="space-y-6">
      <div
        v-for="(question, index) in generatedQuestions"
        :key="question.id || index"
        class="question-card"
      >
        <div class="question-content">
          <p class="question-title">{{ question.prompt }}</p>

          <div class="space-y-3 mb-4">
            <div
              v-for="(item, itemIndex) in question.itemTOS"
              :key="itemIndex"
              class="item-row"
            >
              <div
                v-if="question.type === 'TRUE_OR_FALSE'"
                class="flex-1 items-center gap-3"
              >
                <i
                  :class="[
                    item.correct
                      ? 'pi pi-check text-green-600'
                      : 'pi pi-times text-h-error-400',
                    'mr-2',
                  ]"
                >
                </i>
                <span class="item-label">
                  Item {{ getItemLabel(itemIndex, question.type) }}:
                </span>
                <span class="item-text ml-1">{{ item.answerText }}</span>
              </div>
              <template v-else>
                <div class="item-icon">
                  <i
                    v-if="item.correct"
                    class="pi pi-check text-green-600 text-sm"
                  ></i>
                  <i v-else class="pi pi-times text-h-error-400 text-sm"></i>
                </div>

                <div class="flex-1">
                  <span class="item-label">
                    Item {{ getItemLabel(itemIndex, question.type) }}:
                  </span>
                  <span class="item-text">{{ item.answerText }}</span>
                </div>
              </template>
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-2">
            <Button
              class="p-button-sm p-button-text question-edit-btn"
              icon="pi pi-pencil"
              :label="$t('labels.edit')"
              @click="editQuestion(question, index)"
              :disabled="loadingStates[index] || savingQuestion"
            />
            <Button
              class="p-button-sm p-button-text p-button-danger"
              icon="pi pi-trash"
              :label="$t('labels.delete')"
              @click="deleteGeneratedQuestion(question, index)"
              :loading="loadingStates[index]"
              :disabled="savingQuestion"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="generatedQuestions.length === 0" class="text-center py-8">
      <EmptyData
        :data="true"
        :label="
          $t('labels.generic', {
            pt: 'Nenhuma questão gerada ainda',
            en: 'No questions generated yet',
          })
        "
        image="empty_questions.svg"
        imgSize="h-24"
      />
    </div>

    <Dialog
      v-model:visible="addQuestionModal"
      :draggable="false"
      :breakpoints="{ '960px': '95vw', '640px': '95vw' }"
      :style="{ width: '50vw', minWidth: '400px' }"
      @hide="closeAddQuestionModal"
      modal
    >
      <template #header>
        <p class="text-lg font-bold w-full text-center">
          {{ $t("quiz.teacher.edit") }}
        </p>
      </template>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{
              $t("labels.generic", {
                pt: "Tipo de questão",
                en: "Question type",
              })
            }}
          </label>
          <Dropdown
            v-model="newQuestion.type"
            :options="questionTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione o tipo"
            class="w-full"
            :disabled="true"
            :showClear="false"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("labels.generic", { pt: "Pergunta", en: "Question" }) }} *
          </label>
          <Textarea
            v-model="newQuestion.prompt"
            rows="3"
            class="w-full"
            :placeholder="
              $t('labels.generic', {
                pt: 'Digite sua pergunta aqui',
                en: 'Type your question here',
              })
            "
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{
              $t("labels.generic", { pt: "Alternativas", en: "Alternatives" })
            }}
          </label>

          <div v-if="newQuestion.type === 'TRUE_OR_FALSE'" class="space-y-6">
            <div
              v-for="(item, index) in newQuestion.itemTOS"
              :key="`tf-item-${index}`"
              class="border border-gray-200 rounded-lg p-4"
            >
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-base font-medium text-gray-900">
                  Item {{ getItemLabel(index, newQuestion.type) }}*
                </h4>
                <Button
                  v-if="newQuestion.itemTOS.length > 2"
                  class="p-button-sm p-button-outlined p-button-danger"
                  icon="pi pi-trash"
                  @click="removeAlternative(index)"
                />
              </div>
              <div class="mb-4">
                <Textarea
                  v-model="item.answerText"
                  rows="3"
                  class="w-full"
                  :placeholder="
                    $t('labels.generic', {
                      pt: 'Digite aqui o item da questão',
                      en: 'Enter the question item here',
                    })
                  "
                />
              </div>
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Resposta*
                </label>
                <div class="select-button-group">
                  <button
                    type="button"
                    :class="[
                      'select-button',
                      item.correct === true ? 'active' : 'inactive',
                    ]"
                    @click="setTrueFalseAnswer(index, true)"
                  >
                    Verdadeiro
                  </button>
                  <button
                    type="button"
                    :class="[
                      'select-button',
                      item.correct === false ? 'active' : 'inactive',
                    ]"
                    @click="setTrueFalseAnswer(index, false)"
                  >
                    Falso
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Explicação sobre o item*
                </label>
                <Textarea
                  v-model="item.answerDescription"
                  rows="3"
                  class="w-full"
                  :placeholder="
                    $t('labels.generic', {
                      pt: 'Digite aqui a explicação sobre o item',
                      en: 'Enter the explanation about the item here',
                    })
                  "
                />
              </div>
            </div>
            <div
              v-if="newQuestion.itemTOS.length < 5"
              class="flex justify-start"
            >
              <Button
                class="btn-outline p-button-sm gap-1"
                icon="pi pi-plus"
                :label="
                  $t('labels.generic', {
                    pt: 'Criar item',
                    en: 'Create item',
                  })
                "
                @click="addAlternative"
              />
            </div>
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="(item, index) in newQuestion.itemTOS"
              :key="`item-${index}`"
              class="space-y-2"
            >
              <div class="flex items-center gap-3">
                <div class="relative flex items-center">
                  <RadioButton
                    v-model="correctAnswerIndex"
                    :inputId="`correct-${index}`"
                    :value="index"
                    :class="
                      correctAnswerIndex === index ? 'correct-selected' : ''
                    "
                  />
                  <i
                    v-if="correctAnswerIndex === index"
                    class="pi pi-check check-icon absolute"
                  ></i>
                </div>

                <InputText
                  v-model="item.answerText"
                  :placeholder="`${$t('labels.generic', {
                    pt: 'Alternativa',
                    en: 'Alternative',
                  })} ${getItemLabel(index, newQuestion.type)}`"
                  :class="[
                    'flex-1',
                    item.correct
                      ? 'border-green-500 focus:border-green-600 focus:ring-green-500'
                      : 'border-gray-300',
                  ]"
                />
              </div>
              <div class="ml-8">
                <Textarea
                  v-model="item.answerDescription"
                  rows="2"
                  class="w-full"
                  :placeholder="
                    $t('labels.generic', {
                      pt: 'Explicação sobre o item',
                      en: 'Item explanation',
                    })
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <Button
            class="btn-outline p-button-sm"
            :label="$t('labels.cancel')"
            @click="closeAddQuestionModal"
            :disabled="savingQuestion"
          />
          <Button
            class="btn-primary p-button-sm"
            :label="$t('labels.generic', { pt: 'Atualizar', en: 'Update' })"
            @click="saveQuestion"
            :loading="savingQuestion"
            :disabled="!isQuestionValid"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:visible="deleteConfirmModal"
      :draggable="false"
      :style="{ width: '400px' }"
      modal
    >
      <template #header>
        <p class="text-lg font-bold w-full text-center">
          {{
            $t("labels.generic", {
              pt: "Confirmar exclusão",
              en: "Confirm deletion",
            })
          }}
        </p>
      </template>

      <p class="text-center">
        {{
          $t("labels.generic", {
            pt: "Tem certeza que deseja excluir esta questão?",
            en: "Are you sure you want to delete this question?",
          })
        }}
      </p>

      <template #footer>
        <div class="flex justify-end gap-3">
          <Button
            class="btn-outline p-button-sm"
            :label="$t('labels.cancel')"
            @click="cancelDelete"
            :disabled="deletingQuestion"
          />
          <Button
            class="btn-primary p-button-sm"
            :label="$t('labels.delete')"
            @click="confirmDeleteQuestion"
            :loading="deletingQuestion"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { ToastFactory } from "@/utils/toast-factory.util";

interface GeneratedQuestion {
  id?: string;
  prompt: string;
  type: string;
  itemTOS: Array<{
    id?: string;
    answerText: string;
    correct: boolean;
    answerType: string;
    answerDescription?: string;
  }>;
  contents?: any[];
  materialReferences?: any[];
  promptType?: string;
  openQuestion?: boolean;
}

export default defineComponent({
  name: "GeneratedQuizQuestions",
  emits: ["questionsUpdated", "allQuestionsSaved"],
  props: {
    questions: {
      type: Array as PropType<GeneratedQuestion[]>,
      default: () => [],
    },
    role: {
      type: String,
      default: "",
    },
    quizId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      generatedQuestions: [] as GeneratedQuestion[],
      loadingStates: {} as Record<number, boolean>,
      savingAllQuestions: false,
      addQuestionModal: false,
      deleteConfirmModal: false,
      editingQuestion: false,
      editingIndex: -1,
      questionToDelete: null as GeneratedQuestion | null,
      questionToDeleteIndex: -1,
      savingQuestion: false,
      deletingQuestion: false,
      correctAnswerIndex: 0,
      newQuestion: {
        prompt: "",
        type: "DEFAULT",
        itemTOS: [
          {
            answerText: "",
            correct: false,
            answerType: "TEXT",
            answerDescription: "",
          },
          {
            answerText: "",
            correct: false,
            answerType: "TEXT",
            answerDescription: "",
          },
          {
            answerText: "",
            correct: false,
            answerType: "TEXT",
            answerDescription: "",
          },
          {
            answerText: "",
            correct: false,
            answerType: "TEXT",
            answerDescription: "",
          },
        ],
        contents: [],
        materialReferences: [],
        promptType: "TEXT",
        openQuestion: false,
      } as GeneratedQuestion,
      questionTypeOptions: [
        { label: "Múltipla Escolha", value: "DEFAULT" },
        { label: "Verdadeiro ou Falso", value: "TRUE_OR_FALSE" },
      ],
    };
  },
  computed: {
    isQuestionValid() {
      const hasPrompt = this.newQuestion.prompt.trim() !== "";

      if (this.newQuestion.type === "TRUE_OR_FALSE") {
        return (
          hasPrompt &&
          this.newQuestion.itemTOS.some((item) => item.correct) &&
          this.newQuestion.itemTOS.every(
            (item) =>
              item.answerText.trim() !== "" &&
              item.answerDescription &&
              item.answerDescription.trim() !== ""
          )
        );
      } else {
        return (
          hasPrompt &&
          this.newQuestion.itemTOS.length === 4 &&
          this.newQuestion.itemTOS.every(
            (item) =>
              item.answerText.trim() !== "" &&
              item.answerDescription &&
              item.answerDescription.trim() !== ""
          ) &&
          this.newQuestion.itemTOS.some((item) => item.correct)
        );
      }
    },
  },
  watch: {
    questions: {
      handler(newQuestions: GeneratedQuestion[]) {
        this.generatedQuestions = [...newQuestions];
      },
      immediate: true,
      deep: true,
    },
    correctAnswerIndex: {
      handler(newIndex: number) {
        if (this.newQuestion.type !== "TRUE_OR_FALSE") {
          this.newQuestion.itemTOS.forEach((item, index) => {
            item.correct = index === newIndex;
          });
        }
      },
      immediate: false,
    },
  },
  methods: {
    getQuestionTypeLabel(type: string): string {
      switch (type) {
        case "DEFAULT":
          return "Múltipla Escolha";
        case "TRUE_OR_FALSE":
          return "Verdadeiro ou Falso";
        default:
          return type || "Não definido";
      }
    },

    getItemLabel(index: number, questionType: string): string {
      if (questionType === "TRUE_OR_FALSE") {
        return String.fromCharCode(65 + index);
      }
      return String.fromCharCode(65 + index);
    },

    closeAddQuestionModal() {
      this.addQuestionModal = false;
      this.editingQuestion = false;
      this.editingIndex = -1;
      this.savingQuestion = false;

      setTimeout(() => {
        this.resetNewQuestion();
      }, 100);
    },

    resetNewQuestion() {
      this.newQuestion = {
        prompt: "",
        type: "DEFAULT",
        itemTOS: [
          {
            answerText: "",
            correct: false,
            answerType: "TEXT",
            answerDescription: "",
          },
          {
            answerText: "",
            correct: false,
            answerType: "TEXT",
            answerDescription: "",
          },
          {
            answerText: "",
            correct: false,
            answerType: "TEXT",
            answerDescription: "",
          },
          {
            answerText: "",
            correct: false,
            answerType: "TEXT",
            answerDescription: "",
          },
        ],
        contents: [],
        materialReferences: [],
        promptType: "TEXT",
        openQuestion: false,
      };
      this.correctAnswerIndex = 0;
    },

    editQuestion(question: GeneratedQuestion, index: number) {
      this.editingQuestion = true;
      this.editingIndex = index;

      this.newQuestion = JSON.parse(JSON.stringify(question));

      if (this.newQuestion.type === "TRUE_OR_FALSE") {
        if (this.newQuestion.itemTOS.length < 2) {
          while (this.newQuestion.itemTOS.length < 2) {
            this.newQuestion.itemTOS.push({
              answerText: "",
              correct: false,
              answerType: "TEXT",
              answerDescription: "",
            });
          }
        }
        this.newQuestion.itemTOS.forEach((item) => {
          if (!item.answerDescription) {
            item.answerDescription = "";
          }
        });
      } else {
        if (this.newQuestion.itemTOS.length < 4) {
          while (this.newQuestion.itemTOS.length < 4) {
            this.newQuestion.itemTOS.push({
              answerText: "",
              correct: false,
              answerType: "TEXT",
              answerDescription: "",
            });
          }
        } else if (this.newQuestion.itemTOS.length > 4) {
          this.newQuestion.itemTOS = this.newQuestion.itemTOS.slice(0, 4);
        }

        this.newQuestion.itemTOS.forEach((item) => {
          if (!item.answerDescription) {
            item.answerDescription = "";
          }
        });

        const correctIndex = this.newQuestion.itemTOS.findIndex(
          (item) => item.correct
        );
        this.correctAnswerIndex = correctIndex >= 0 ? correctIndex : 0;
      }

      this.addQuestionModal = true;
    },

    onTrueFalseChange(index: number) {
      this.$nextTick(() => {
        this.newQuestion.itemTOS.forEach((item, i) => {
          item.correct = i === index;
        });
      });
    },

    setTrueFalseAnswer(index: number, isTrue: boolean) {
      this.newQuestion.itemTOS[index].correct = isTrue;
    },

    addAlternative() {
      if (
        this.newQuestion.type === "TRUE_OR_FALSE" &&
        this.newQuestion.itemTOS.length < 5
      ) {
        this.newQuestion.itemTOS.push({
          answerText: "",
          correct: false,
          answerType: "TEXT",
          answerDescription: "",
        });
      }
    },

    removeAlternative(index: number) {
      if (
        this.newQuestion.type === "TRUE_OR_FALSE" &&
        this.newQuestion.itemTOS.length > 2
      ) {
        this.newQuestion.itemTOS.splice(index, 1);
      }
    },

    saveQuestion() {
      if (!this.isQuestionValid) return;

      this.savingQuestion = true;

      setTimeout(() => {
        if (this.newQuestion.type !== "TRUE_OR_FALSE") {
          this.newQuestion.itemTOS.forEach((item, index) => {
            item.correct = index === this.correctAnswerIndex;
          });
        }

        if (this.editingQuestion) {
          this.generatedQuestions[this.editingIndex] = { ...this.newQuestion };

          this.$toast.add(
            ToastFactory.success(
              this.$t("labels.generic", {
                pt: "Questão atualizada com sucesso!",
                en: "Question updated successfully!",
              })
            )
          );
        }

        this.savingQuestion = false;
        this.closeAddQuestionModal();
        this.$emit("questionsUpdated", this.generatedQuestions);
      }, 1000);
    },

    deleteGeneratedQuestion(question: GeneratedQuestion, index: number) {
      if (!question || index < 0) return;

      this.questionToDelete = question;
      this.questionToDeleteIndex = index;
      this.deleteConfirmModal = true;
    },

    cancelDelete() {
      this.deleteConfirmModal = false;
      this.questionToDelete = null;
      this.questionToDeleteIndex = -1;
    },

    confirmDeleteQuestion() {
      if (this.questionToDelete && this.questionToDeleteIndex >= 0) {
        this.deletingQuestion = true;

        this.loadingStates = {
          ...this.loadingStates,
          [this.questionToDeleteIndex]: true,
        };

        setTimeout(() => {
          this.generatedQuestions.splice(this.questionToDeleteIndex, 1);

          this.deletingQuestion = false;
          this.deleteConfirmModal = false;
          this.questionToDelete = null;
          this.questionToDeleteIndex = -1;
          this.loadingStates = {};

          this.$toast.add(
            ToastFactory.success(
              this.$t("labels.generic", {
                pt: "Questão excluída com sucesso!",
                en: "Question deleted successfully!",
              })
            )
          );

          this.$emit("questionsUpdated", this.generatedQuestions);
        }, 1000);
      }
    },
  },
});
</script>

<style scoped>
.generated-quiz-questions {
  @apply space-y-4;
}

.btn-success {
  @apply bg-green-600 hover:bg-green-700 border-green-600 text-white;
}

.question-card {
  @apply bg-white border-b-4 border-gray-800 rounded-lg shadow-sm;
}

.question-content {
  @apply px-4 py-4;
}

.question-title {
  @apply text-lg font-medium text-gray-900 mb-4;
}

.item-row {
  @apply flex items-start gap-3;
}

.item-icon {
  @apply mt-1;
}

.item-label {
  @apply text-gray-800 font-medium mr-2;
}

.item-text {
  @apply text-gray-900 font-medium;
}

.question-edit-btn {
  @apply text-gray-800 hover:bg-gray-100 hover:text-gray-900;
}

.question-edit-btn .p-button-icon {
  @apply text-gray-800;
}

.question-edit-btn:hover .p-button-icon {
  @apply text-gray-900;
}

.correct-selected :deep(.p-radiobutton-box),
.correct-selected :deep(.p-checkbox-box),
.correct-selected :deep(.p-radiobutton-box.p-highlight),
.correct-selected :deep(.p-checkbox-box.p-highlight) {
  @apply bg-green-500 border-green-500;
}

.check-icon {
  @apply text-white text-xs;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 10;
}

.relative {
  position: relative;
}

.select-button-group {
  display: flex;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #d1d5db;
}

.select-button {
  flex: 1;
  padding: 8px 16px;
  background-color: #f9fafb;
  color: #374151;
  border: none;
  border-right: 1px solid #d1d5db;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
}

.select-button:last-child {
  border-right: none;
}

.select-button.active {
  background-color: #3b82f6;
  color: white;
}

.select-button.inactive:hover {
  background-color: #f3f4f6;
}

.select-button.active:hover {
  background-color: #2563eb;
}
</style>
